import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Monitor, Smartphone, ShieldCheck, Trash2, MapPin, Clock } from "lucide-react";
import { API } from "./crm/shared";

const parseUa = (ua = "") => {
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Navegador";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "Sistema";
  const mobile = /Android|iPhone|iPad|Mobile/.test(ua);
  return { label: `${browser} · ${os}`, mobile };
};

const fmt = (d) => (d ? new Date(d).toLocaleString("es-PA", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const AdminDevices = ({ headers }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/devices`, { headers });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudieron cargar los dispositivos");
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (d) => {
    if (!window.confirm(`¿Revocar el dispositivo "${parseUa(d.ua).label}" (IP ${d.ip || "—"})? La próxima vez que inicie sesión desde ahí recibirás un aviso de dispositivo nuevo.`)) return;
    setBusy(d.fp);
    try {
      await axios.delete(`${API}/auth/devices/${d.fp}`, { headers });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo revocar");
    } finally {
      setBusy("");
    }
  };

  if (!data && !error) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-black/30" /></div>;

  return (
    <div data-testid="admin-devices" className="bg-white border border-black/[0.06] rounded-2xl p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center shrink-0"><ShieldCheck size={18} className="text-[#0fe814]" /></div>
        <div>
          <h2 className="text-[16px] font-extrabold text-black">Dispositivos conocidos</h2>
          <p className="text-[12px] font-medium text-black/50">Equipos desde los que has iniciado sesión con éxito. Revoca los que no reconozcas; un dispositivo revocado volverá a generar un aviso de "nuevo inicio de sesión" si se usa otra vez.</p>
        </div>
      </div>
      {error && <p data-testid="devices-error" className="mb-4 text-[12px] font-bold text-[#ff603e]">{error}</p>}
      {data?.devices?.length === 0 && <p data-testid="devices-empty" className="text-[13px] font-semibold text-black/45 py-6 text-center">Aún no hay dispositivos registrados.</p>}
      <ul className="space-y-2">
        {(data?.devices || []).map((d) => {
          const ua = parseUa(d.ua);
          const isCurrent = d.fp === data.current_fp;
          const Icon = ua.mobile ? Smartphone : Monitor;
          return (
            <li key={d.fp} data-testid={`device-${d.fp}`} className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border px-4 py-3 ${isCurrent ? "border-[#0fb013]/40 bg-[#0fe814]/5" : "border-black/[0.06] bg-[#fafcf9]"}`}>
              <Icon size={20} className="text-black/50 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-black">
                  {ua.label}{" "}
                  {isCurrent && <span data-testid="device-current-badge" className="ml-1 text-[10px] font-bold text-black bg-[#0fe814] px-2 py-0.5 rounded-full">Este dispositivo</span>}
                </p>
                <p className="text-[11px] font-medium text-black/45 truncate" title={d.ua}>{d.ua}</p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold text-black/60">
                  <span className="inline-flex items-center gap-1"><MapPin size={11} /> IP remota: <b data-testid="device-ip" className="text-black">{d.ip || "desconocida"}</b></span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> Último acceso: {fmt(d.last_seen)}</span>
                  <span className="inline-flex items-center gap-1">Primer acceso: {fmt(d.first_seen)}</span>
                </div>
              </div>
              <button
                data-testid={`device-revoke-${d.fp}`}
                onClick={() => revoke(d)}
                disabled={busy === d.fp}
                className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-[#ff603e] bg-[#ff603e]/10 hover:bg-[#ff603e]/20 px-3.5 py-2 rounded-full transition-colors disabled:opacity-50"
              >
                {busy === d.fp ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Revocar
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AdminDevices;
